import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Send as SendIcon,
  Clear as ClearIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { fetchAvailableFiles, createJob, createBatchJobs } from '../store/slices/jobsSlice';
import PageHeader from '../components/PageHeader';
import { fetchTemplates } from '../store/slices/templatesSlice';
import { fetchEncodingProfiles } from '../store/slices/encodingProfilesSlice';
import api from '../services/api';
import { useUploadManager } from '../contexts/UploadManagerContext';
import {
  isValidFilename, sanitizeFilename,
  isValidName, sanitizeName, validateEmail,
} from '../utils/createJobValidation';
import { decideSourceUpload } from '../utils/sourceUploadDecision';

// Validators moved to utils/createJobValidation.js (v10.4.32). Same
// regexes, single tested source. Imported below.

function CreateJob() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { enqueue } = useUploadManager();
  
  const { availableFiles: allAvailableFiles } = useSelector((state) => state.jobs);
  // Hide files that the user has flagged as DCP-only on the Sources page.
  const availableFiles = (allAvailableFiles || []).filter(
    (f) => f.available_for_watermark !== false,
  );
  const { templates } = useSelector((state) => state.templates);
  const { profiles: encodingProfiles } = useSelector((state) => state.encodingProfiles);
  const { activeCompanyId, list: myCompanies } = useSelector((state) => state.company);
  // Used in the helper line under the recipient block to remind the
  // operator that the recipient delivery email will CC them at this
  // address. Pulled from the auth slice (already populated by the
  // /api/users/me call on login).
  const userInfo = useSelector((state) => state.auth.userInfo);
  const activeCompany = myCompanies.find((c) => c.id === activeCompanyId);
  // Single-tenant view: the only company they can create a job for is their own.
  const companies = activeCompany ? [activeCompany] : [];
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  // ?source=<filename> pre-selects a file from the Sources page's
  // QC drawer. We can't apply it during initial useState because
  // availableFiles isn't loaded yet — the effect below applies it
  // once both the URL param and the available-files list are ready.
  const [searchParams, setSearchParams] = useSearchParams();
  const preselectSource = searchParams.get('source');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedEncodingProfile, setSelectedEncodingProfile] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  // v10.21.0: optional project label + custom email message. Both
  // batch-level — every recipient gets the same project_name on the
  // email subject and the same paragraph above the watch/download
  // block. Filed from bug #65 (Marty, /screeners/create).
  const [projectNameOverride, setProjectNameOverride] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  // Additional recipients beyond the primary. Each gets its own
  // uniquely-watermarked encode (one job per recipient × source) so
  // a leak can be traced to a specific person — that's the whole
  // point of watermarking. Operator clicks "+ Add recipient" to
  // reveal more rows; rows with both fields empty are silently
  // skipped on submit.
  const [additionalRecipients, setAdditionalRecipients] = useState([]); // [{name, email}]
  // Per-job share expiration override. Number of days from now;
  // backend clamps to [1, 365] and treats null/0 as "use system
  // default" (currently 30). UI default 30 matches the system default
  // so the operator's typical action is "leave it alone."
  const [shareExpiresDays, setShareExpiresDays] = useState(30);
  // Default OFF — recipients get streaming-only unless the operator opts
  // them into the original .mp4 download.
  const [allowDownload, setAllowDownload] = useState(false);
  const [allowReviews, setAllowReviews] = useState(false);
  // Frame capture (v9.20.0) — clean stills from the source via the
  // watch player. Off by default; turning it on shows a leak-warning
  // alert (the captured frames are CLEAN — no overlay).
  const [allowFrameCapture, setAllowFrameCapture] = useState(false);
  // v10.21.17: per-job NDA gate. Default ON — every recipient sees
  // the confidentiality prompt on the landing page before they can
  // watch or download. Operator opts OUT for trusted recipients
  // (internal review, talent agent on retainer, etc.) where the
  // prompt is friction without value.
  const [ndaRequired, setNdaRequired] = useState(true);
  // Critical opt-out — when ON the encoder skips the drawtext overlay
  // entirely. The screener ships with NO recipient identifier baked in,
  // which means a leak can't be traced back to the recipient. Off by
  // default; submit shows a confirmation dialog before we accept.
  const [disableWatermark, setDisableWatermark] = useState(false);
  const [disableWarnDialog, setDisableWarnDialog] = useState(null); // { payload, batch }
  // Operator's choice of how the recipient finds out the screener is
  // ready. Default ON: the system auto-emails the recipient when the
  // encode + sync are done (current historical behavior). OFF: the
  // share + link still get auto-created (so a Library row "Copy link"
  // works immediately) but the email is held until the operator hits
  // "Send delivery email now" or composes a richer message themselves.
  const [autoSendEmail, setAutoSendEmail] = useState(true);
  // Per-source channel info (loaded on selection, used to surface a
  // 5.1 → 2.0 fold-down option). Map of filename → { channels, has_qc }.
  const [sourceAudio, setSourceAudio] = useState({});
  // Operator-controlled fold-down. **Default OFF** — multichannel
  // sources keep their original 5.1/7.1 mix; the operator opts in
  // per job if they want stereo (e.g. for browser-only reviewers
  // whose hardware doesn't render surround cleanly). Pre-v10.4.32
  // this auto-defaulted ON whenever channels>2 — wrong default,
  // assumed compat over fidelity.
  const [downmixToStereo, setDownmixToStereo] = useState(false);
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Uploads are owned by the global UploadManager (useUploadManager) so they
  // survive page navigation and can be cancelled from the floating overlay.
  // Local "uploading" flag is no longer needed.

  const [defaultTemplateId, setDefaultTemplateId] = useState(null);
  // Duplicate-recipient confirmation dialog. Backend returns 409 with
  // {code:"duplicate_recipient_source", duplicates:[...]} when the same
  // recipient already has a watermark of one of the selected sources in
  // the last 30 days. We surface the colliding rows and let the operator
  // confirm to proceed with force_duplicate=true.
  const [dupDialog, setDupDialog] = useState(null); // { duplicates, payload, batch }

  useEffect(() => {
    dispatch(fetchAvailableFiles());
    dispatch(fetchTemplates());
    dispatch(fetchEncodingProfiles());
    fetchDefaultTemplate().then(id => setDefaultTemplateId(id));
  }, [dispatch]);

  // Apply ?source=<filename> once the available-files list is loaded.
  // We then strip the param so a refresh doesn't re-apply (which would
  // override the operator's edits if they removed the file from the
  // selection on purpose). Filename mismatch (typo, deleted source)
  // silently no-ops — the dropdown will just show empty.
  useEffect(() => {
    if (!preselectSource) return;
    if (!availableFiles.length) return;
    const match = availableFiles.find((f) => f.filename === preselectSource);
    if (match && !selectedFiles.includes(match.filename)) {
      setSelectedFiles([match.filename]);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('source');
      return next;
    }, { replace: true });
  }, [preselectSource, availableFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select the active company (the only one in scope)
  useEffect(() => {
    if (activeCompanyId && !selectedCompany) {
      // Default to the full-name option for the active company.
      setSelectedCompany(`${activeCompanyId}|full`);
    }
  }, [activeCompanyId, selectedCompany]);

  // v10.21.5: pre-fill Project Name with the active campaign's name so
  // the delivery email subject reads "Your screener for {Film Title}"
  // out of the box. The operator-typed override path still wins —
  // they can edit or clear the field before submit. If the user has
  // already typed something (e.g. opened from a deep-link with a
  // saved draft), don't stomp it.
  //
  // v10.21.7: two-stage lookup. First try the campaign id in
  // localStorage (the picked-campaign signal). If that's missing or
  // the GET fails, fall back to /api/campaigns (list) and use the
  // sole campaign if there's exactly one. Operator-reported case: the
  // field stayed blank for a user whose activeCampaignId wasn't set
  // somehow (cleared cache, super-admin view, multi-window race).
  const projectNameTouched = useRef(false);
  useEffect(() => {
    if (projectNameTouched.current) return;
    if (projectNameOverride) return;
    const cid = (() => {
      try { return localStorage.getItem('activeCampaignId'); }
      catch (_e) { return null; }
    })();
    let cancelled = false;
    const apply = (name) => {
      if (cancelled) return;
      const clean = (name || '').trim();
      if (clean && !projectNameTouched.current) {
        setProjectNameOverride(clean);
      }
    };
    if (cid) {
      api.get(`/api/campaigns/${cid}`)
        .then((r) => apply(r.data?.name))
        .catch(() => {
          // Fall back to list-lookup if the single-id fetch fails
          // (stale id, deleted campaign, etc.). Best-effort.
          api.get('/api/campaigns')
            .then((r) => {
              const arr = Array.isArray(r.data) ? r.data : [];
              if (arr.length === 1) apply(arr[0]?.name);
            })
            .catch(() => { /* swallow */ });
        });
    } else {
      // No picked-campaign id. If the tenant has exactly one campaign,
      // that's unambiguously the campaign to use.
      api.get('/api/campaigns')
        .then((r) => {
          const arr = Array.isArray(r.data) ? r.data : [];
          if (arr.length === 1) apply(arr[0]?.name);
        })
        .catch(() => { /* swallow */ });
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const fetchDefaultTemplate = async () => {
    try {
      const response = await api.get('/api/settings/default_template_id');
      if (response.data && response.data.value) {
        // Store the default but don't set it yet
        return response.data.value;
      }
    } catch (_err) {
      // If the setting doesn't exist, that's okay
      // If the setting doesn't exist, that's okay
    }
    return null;
  };

  // Set default encoding profile when profiles are loaded
  useEffect(() => {
    if (encodingProfiles.length > 0 && !selectedEncodingProfile) {
      const defaultProfile = encodingProfiles.find(p => p.is_default);
      if (defaultProfile) {
        setSelectedEncodingProfile(defaultProfile.id);
      }
    }
  }, [encodingProfiles, selectedEncodingProfile]);

  // Load per-source audio channel info from the QC sidecar so we can
  // surface "Source has 5.1 audio — fold down to stereo?" inline. The
  // QC report already extracts this; re-fetching is ~1KB per source
  // and only fires when the selection changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = { ...sourceAudio };
      const filenames = selectedFiles.filter((f) => !next[f]);
      for (const fn of filenames) {
        try {
          const { data } = await api.get(`/api/jobs/available-files/${encodeURIComponent(fn)}/qc`);
          const checks = data?.report?.checks || [];
          const acheck = checks.find((c) => c.name === 'Audio channels');
          // Match e.g. "6 channels" → 6
          const m = (acheck?.message || '').match(/(\d+)\s*channels/i);
          const channels = m ? parseInt(m[1], 10) : null;
          if (!cancelled) {
            next[fn] = { channels, has_qc: !!acheck };
          }
        } catch (_) {
          if (!cancelled) next[fn] = { channels: null, has_qc: false };
        }
      }
      // Drop entries for files no longer selected
      const cleaned = {};
      for (const f of selectedFiles) {
        if (next[f]) cleaned[f] = next[f];
      }
      if (!cancelled) setSourceAudio(cleaned);
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  // (Removed in v10.4.32: the effect that auto-defaulted
  // downmixToStereo=true whenever any selected source had >2
  // channels. Operator feedback: the fold-down should be the
  // operator's call, not implied. Multichannel reviewers want their
  // 5.1 mix preserved; the default ON was eating that fidelity
  // silently. The toggle is now plain opt-in — when the picker
  // detects a multichannel source the toggle becomes visible, but
  // unchecked. Operator ticks it per job if they want stereo.)

  // Set default template when templates are loaded
  useEffect(() => {
    if (templates.length > 0 && defaultTemplateId && !selectedTemplate) {
      const templateExists = templates.find(t => t.id === parseInt(defaultTemplateId));
      if (templateExists) {
        setSelectedTemplate(defaultTemplateId);
      }
    }
  }, [templates, defaultTemplateId, selectedTemplate]);

  const handleToggleFile = (filename) => {
    // Check if filename is valid
    if (!isValidFilename(filename)) {
      setError(`The file "${filename}" contains illegal characters. Please rename it to: ${sanitizeFilename(filename)}`);
      return;
    }
    
    setSelectedFiles((prev) =>
      prev.includes(filename)
        ? prev.filter((f) => f !== filename)
        : [...prev, filename]
    );
  };

  const handleSelectAll = () => {
    if (selectedFiles.length === availableFiles.length) {
      setSelectedFiles([]);
    } else {
      // Only select files with valid filenames
      const validFiles = availableFiles
        .filter((f) => isValidFilename(f.filename))
        .map((f) => f.filename);
      
      const invalidFiles = availableFiles.filter((f) => !isValidFilename(f.filename));
      if (invalidFiles.length > 0) {
        setError(`${invalidFiles.length} file(s) skipped due to illegal characters in filenames`);
      }
      
      setSelectedFiles(validFiles);
    }
  };

  // Build the full recipients list for the current submit. The primary
  // (recipientName/recipientEmail) is always row 0; additionalRecipients
  // contributes rows with both fields filled. Empty additional rows are
  // dropped silently so the operator can leave the +Add slots blank.
  const collectRecipients = () => {
    const list = [{ name: recipientName.trim(), email: recipientEmail.trim() }];
    additionalRecipients.forEach((r) => {
      const name = (r.name || '').trim();
      const email = (r.email || '').trim();
      if (name || email) list.push({ name, email });
    });
    return list;
  };

  const handleSubmit = async () => {
    setError('');

    if (!selectedCompany) {
      setError('Please select a production company');
      return;
    }

    if (!selectedTemplate) {
      setError('Please select a watermark template');
      return;
    }

    const recipients = collectRecipients();
    // Per-recipient validation. Surface the failing row's index so the
    // operator can find it on a long form.
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      const label = i === 0 ? 'Primary recipient' : `Recipient #${i + 1}`;
      if (!r.name) {
        setError(`${label}: name is required`);
        return;
      }
      if (!isValidName(r.name)) {
        const sanitized = sanitizeName(r.name);
        setError(`${label}: name contains invalid characters. Suggested: ${sanitized}`);
        return;
      }
      if (!r.email) {
        setError(`${label}: email is required`);
        return;
      }
      const emailValidation = validateEmail(r.email);
      if (!emailValidation.valid) {
        setError(`${label}: ${emailValidation.error}`);
        return;
      }
    }
    // Catch the easy mistake of pasting the same address twice.
    const seen = new Set();
    for (const r of recipients) {
      const key = r.email.toLowerCase();
      if (seen.has(key)) {
        setError(`Duplicate recipient email: ${r.email}`);
        return;
      }
      seen.add(key);
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    setLoading(true);

    // selectedCompany is "<id>|full" or "<id>|short" (or just "<id>" for
    // backward compat). Split into the numeric id and the label style.
    const [rawId, rawStyle] = String(selectedCompany).split('|');
    const baseJobData = {
      company_id: parseInt(rawId),
      company_label_style: rawStyle === 'short' ? 'short' : 'full',
      template_id: parseInt(selectedTemplate),
      allow_download: allowDownload,
      allow_reviews: allowReviews,
      allow_frame_capture: allowFrameCapture,
      nda_required: ndaRequired,
      priority: priority,
      encoding_profile_id: selectedEncodingProfile ? parseInt(selectedEncodingProfile) : null,
      audio_downmix_stereo: !!downmixToStereo,
      disable_watermark: !!disableWatermark,
      auto_send_email: !!autoSendEmail,
      // Per-job override; backend clamps to [1, 365]. Null/0 → system default.
      share_expires_days: shareExpiresDays > 0 ? Math.min(365, Math.max(1, parseInt(shareExpiresDays, 10) || 30)) : null,
      // v10.21.0: optional batch label (overrides Package.project_name
      // → email subject) and custom message that prepends to the
      // delivery email body. Empty strings → null so the backend
      // falls back to legacy auto-derived behavior.
      project_name_override: projectNameOverride.trim() || null,
      email_message: emailMessage.trim() || null,
    };
    const batch = selectedFiles.length !== 1;
    const buildPayload = (recipient) => {
      const payload = {
        ...baseJobData,
        recipient_name: recipient.name,
        recipient_email: recipient.email,
      };
      return batch
        ? { source_filenames: selectedFiles, ...payload }
        : { source_filename: selectedFiles[0], ...payload };
    };
    // Disable-overlay confirmation: still gate on the leak warning, but
    // now the dialog has to know we're broadcasting to N recipients.
    if (disableWatermark) {
      setDisableWarnDialog({ recipients, batch, buildPayload });
      setLoading(false);
      return;
    }
    await submitCreateMulti(recipients, batch, buildPayload, false);
  };

  const handleConfirmDisable = async () => {
    if (!disableWarnDialog) return;
    const { recipients, batch, buildPayload } = disableWarnDialog;
    setDisableWarnDialog(null);
    setLoading(true);
    await submitCreateMulti(recipients, batch, buildPayload, false);
  };

  const handleCancelDisable = () => {
    setDisableWarnDialog(null);
  };

  // Fire one create request per recipient. For a single recipient this
  // collapses to the previous "single submit" behaviour. For multiple
  // recipients we serialize the calls so duplicate-detection can still
  // raise on the first conflict and the operator can decide once for
  // the whole batch (forceDuplicate then applies to every recipient on
  // retry).
  const submitCreateMulti = async (recipients, batch, buildPayload, forceDuplicate) => {
    const succeeded = [];
    const failed = [];
    for (const r of recipients) {
      const payload = forceDuplicate
        ? { ...buildPayload(r), force_duplicate: true }
        : buildPayload(r);
      try {
        if (batch) {
          await dispatch(createBatchJobs(payload)).unwrap();
        } else {
          await dispatch(createJob(payload)).unwrap();
        }
        succeeded.push(r);
      } catch (err) {
        if (err && typeof err === 'object' && err.code === 'duplicate_recipient_source') {
          // Stop on the first duplicate, surface the dialog. On confirm,
          // we retry the *remaining* (recipient + already-failed) list
          // with force_duplicate=true. Already-succeeded recipients are
          // not re-fired.
          const remaining = recipients.filter((x) => !succeeded.includes(x));
          setDupDialog({
            duplicates: err.duplicates || [],
            recipients: remaining,
            batch,
            buildPayload,
          });
          setLoading(false);
          return;
        }
        failed.push({ recipient: r, err });
      }
    }
    if (failed.length === 0) {
      navigate('/screeners');
      return;
    }
    setError(
      `Created ${succeeded.length} of ${recipients.length}. Failed: ` +
      failed.map((f) => `${f.recipient.email} (${typeof f.err === 'string' ? f.err : (f.err?.message || 'error')})`).join('; '),
    );
    setLoading(false);
  };

  const handleConfirmDuplicate = async () => {
    if (!dupDialog) return;
    const { recipients, batch, buildPayload } = dupDialog;
    setDupDialog(null);
    setLoading(true);
    await submitCreateMulti(recipients, batch, buildPayload, true);
  };

  const handleCancelDuplicate = () => {
    setDupDialog(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatFileDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000); // Convert from Unix timestamp
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    setError('');

    // Per-file decision tree shared with DCP Create via
    // utils/sourceUploadDecision.js — see that file for the matrix.
    for (const file of files) {
      let outcome;
      try {
        outcome = await decideSourceUpload({
          file, existingFiles: allAvailableFiles, product: 'watermarks', api,
        });
      } catch (e) {
        setError(`Failed to enable existing source: ${e.response?.data?.detail || e.message}`);
        continue;
      }
      if (outcome === 'skip') continue;
      if (outcome === 'enabled') {
        await dispatch(fetchAvailableFiles());
        continue;
      }

      enqueue(file, {
        flags: { watermarks: true, dcp: false },
        onComplete: () => setTimeout(() => dispatch(fetchAvailableFiles()), 1000),
      });
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRefreshFiles = () => {
    dispatch(fetchAvailableFiles());
  };

  return (
    <Box>
      <PageHeader
        title="Create Watermark Encode Jobs"
        subtitle="Watermark and encode source files for distribution"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Source Files ({selectedFiles.length} selected)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              ref={fileInputRef}
              id="file-upload-input"
              name="fileUpload"
              type="file"
              multiple
              accept=".mp4,.mov,.avi,.mkv,.webm"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
              aria-label="Upload video files"
            />
            <Tooltip title="Upload video files from your computer" arrow placement="top">
              <span>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload Files
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Refresh the list of available files" arrow placement="top">
              <span>
                <IconButton
                  onClick={handleRefreshFiles}
                  color="primary"
                  aria-label="Refresh file list"
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={selectedFiles.length === availableFiles.length ? 'Clear all selections' : 'Select all valid files'} arrow placement="top">
              <Button onClick={handleSelectAll}>
                {selectedFiles.length === availableFiles.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Tooltip>
          </Box>
        </Box>
        
        
        <Divider sx={{ mb: 2 }} />
        
        <DataGrid
          rows={availableFiles.map(file => ({
            ...file,
            modified: Number(file.modified) || 0 // Ensure modified is a number
          }))}
          columns={[
            {
              field: 'selected',
              headerName: '',
              width: 50,
              sortable: false,
              renderCell: (params) => {
                const isValid = isValidFilename(params.row.filename);
                return (
                  <Tooltip title={isValid ? 'Select this file for watermarking' : 'Cannot select - filename contains illegal characters'} arrow placement="top">
                    <span>
                      <Checkbox
                        id={`file-checkbox-${params.row.filename.replace(/[^a-zA-Z0-9]/g, '-')}`}
                        name={`fileCheckbox_${params.row.filename.replace(/[^a-zA-Z0-9]/g, '-')}`}
                        checked={selectedFiles.includes(params.row.filename)}
                        onChange={() => handleToggleFile(params.row.filename)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={!isValid}
                        inputProps={{
                          'aria-label': `Select ${params.row.filename} for watermarking`
                        }}
                      />
                    </span>
                  </Tooltip>
                );
              },
            },
            {
              field: 'filename',
              headerName: 'File Name',
              flex: 1,
              minWidth: 200,
              renderHeader: () => (
                <Tooltip title="Video file name">
                  <span>File Name</span>
                </Tooltip>
              ),
              renderCell: (params) => {
                const isValid = isValidFilename(params.value);
                return (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    color: isValid ? 'inherit' : 'error.main'
                  }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={!isValid ? `Contains illegal characters. Suggested: ${sanitizeFilename(params.value)}` : params.value}
                    >
                      {params.value}
                    </Typography>
                    {!isValid && (
                      <Tooltip title="Contains illegal characters">
                        <Typography variant="caption" sx={{ ml: 1, color: 'error.main' }}>
                          ⚠️
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                );
              }
            },
            {
              field: 'qc_compliance_per_product',
              headerName: 'WM QC',
              width: 90,
              sortable: false,
              renderHeader: () => (
                <Tooltip title="Watermark-side QC verdict from the Sources page (pass/warn/fail/needs_input). Failing here means the encode will likely fail too — pick a different source or fix QC.">
                  <span>WM QC</span>
                </Tooltip>
              ),
              renderCell: (params) => {
                if (params.row.qc_running) {
                  return <Chip size="small" label="..." variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />;
                }
                const cpp = params.row.qc_compliance_per_product
                  || { watermarks: params.row.qc_compliance };
                const verdict = cpp.watermarks;
                if (!verdict) {
                  return <Typography variant="caption" color="text.disabled">—</Typography>;
                }
                const map = {
                  pass:        { label: '✓',  color: 'success' },
                  warn:        { label: '!',  color: 'warning' },
                  fail:        { label: '✗',  color: 'error'   },
                  needs_input: { label: '?',  color: 'info'    },
                };
                const cfg = map[verdict] || { label: verdict, color: 'default' };
                return (
                  <Tooltip title={`Watermarks QC: ${verdict}`} arrow>
                    <Chip size="small" label={cfg.label} color={cfg.color}
                      sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }} />
                  </Tooltip>
                );
              },
            },
            {
              field: 'size',
              headerName: 'Size',
              width: 120,
              renderHeader: () => (
                <Tooltip title="File size on disk">
                  <span>Size</span>
                </Tooltip>
              ),
              valueFormatter: (value) => formatFileSize(value),
            },
            {
              field: 'modified',
              headerName: 'Modified',
              width: 200,
              renderHeader: () => (
                <Tooltip title="Date and time when file was last modified">
                  <span>Modified</span>
                </Tooltip>
              ),
              valueFormatter: (value) => formatFileDate(value),
              sortComparator: (v1, v2) => {
                // Ensure we're comparing numeric timestamps
                const t1 = Number(v1) || 0;
                const t2 = Number(v2) || 0;
                return t1 - t2;
              },
            },
          ]}
          getRowId={(row) => row.filename}
          onRowClick={(params) => {
            if (isValidFilename(params.row.filename)) {
              handleToggleFile(params.row.filename);
            }
          }}
          checkboxSelection={false}
          disableSelectionOnClick
          hideFooter
          sx={{
            // Auto-size to row count, capped at 5 visible rows. With
            // 5+ files the grid scrolls internally; with fewer it
            // collapses to fit so the form doesn't sit over a half
            // page of empty-state space.
            // ROW_HEIGHT (52) + HEADER_HEIGHT (56) match DataGrid v8
            // defaults; an "empty" state is sized to two rows so the
            // empty-content message has room to render.
            height: 56 + 52 * Math.min(Math.max(availableFiles.length, 2), 5),
            '& .MuiDataGrid-cell': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          localeText={{
            noRowsLabel: 'No video files found in the source directory. Upload files or add them to the source directory configured in Admin → Storage.',
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'modified', sort: 'desc' }], // Default sort by date, newest first
            },
          }}
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Job Settings
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Tooltip title="Select the production company that owns this content" arrow placement="top">
            <FormControl fullWidth>
              <InputLabel id="company-select-label">Production Company</InputLabel>
              <Select
                id="company-select"
                name="companySelect"
                labelId="company-select-label"
                value={selectedCompany || ''}
                label="Production Company"
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                {companies.flatMap((company) => {
                  // Offer both the full name and the short name as separate
                  // selectable options. Both produce the same company_id
                  // when the job is created — the chosen label is what's
                  // recorded in the watermark text and downstream emails.
                  const opts = [
                    <MenuItem key={`${company.id}-full`} value={`${company.id}|full`}>
                      {company.name}
                    </MenuItem>,
                  ];
                  if (company.short_name) {
                    opts.push(
                      <MenuItem key={`${company.id}-short`} value={`${company.id}|short`}>
                        {company.short_name}
                      </MenuItem>,
                    );
                  }
                  return opts;
                })}
              </Select>
            </FormControl>
          </Tooltip>

          <Tooltip title="Choose the watermark design to apply to this video. Pick &quot;Disable overlay&quot; to ship a clean copy with no recipient info baked in (leak-untraceable — confirmation required)." arrow placement="top">
            <FormControl fullWidth>
              <InputLabel id="template-select-label">Watermark Template</InputLabel>
              <Select
                id="template-select"
                name="templateSelect"
                labelId="template-select-label"
                value={disableWatermark ? '__disabled__' : (selectedTemplate || '')}
                label="Watermark Template"
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__disabled__') {
                    // Switch to disable mode — keep selectedTemplate as a
                    // valid FK fallback (default or first active) since the
                    // backend still requires template_id NOT NULL.
                    setDisableWatermark(true);
                    if (!selectedTemplate) {
                      const fallback = (templates.find((t) => t.id === parseInt(defaultTemplateId)) || templates[0]);
                      if (fallback) setSelectedTemplate(fallback.id);
                    }
                  } else {
                    setDisableWatermark(false);
                    setSelectedTemplate(v);
                  }
                }}
              >
                <MenuItem value="__disabled__" sx={{ color: 'error.main', fontWeight: 600 }}>
                  Disable overlay (no recipient info — leak-untraceable)
                </MenuItem>
                <Divider />
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>

          <Tooltip title="Set the processing priority for this job" arrow placement="top">
            <FormControl fullWidth>
              <InputLabel id="priority-select-label">Priority</InputLabel>
              {/* tooltip only wraps the FormControl; warning alert renders below the row */}
              <Select
                id="priority-select"
                name="prioritySelect"
                labelId="priority-select-label"
                value={priority}
                label="Priority"
                onChange={(e) => setPriority(e.target.value)}
              >
                <MenuItem value="low">Low - Processed after other jobs</MenuItem>
                <MenuItem value="normal">Normal - Standard processing order</MenuItem>
                <MenuItem value="high">High - Processed before normal jobs</MenuItem>
                <MenuItem value="urgent">Urgent - Processed immediately</MenuItem>
              </Select>
            </FormControl>
          </Tooltip>

          <Tooltip title="Encoding profile controls the output bitrate / codec quality. Defaults to the workspace default." arrow placement="top">
            <FormControl fullWidth>
              <InputLabel id="encoding-profile-label">Encoding Profile</InputLabel>
              <Select
                id="encoding-profile-select"
                name="encodingProfileSelect"
                labelId="encoding-profile-label"
                value={selectedEncodingProfile || ''}
                label="Encoding Profile"
                onChange={(e) => setSelectedEncodingProfile(e.target.value)}
                renderValue={(selected) => {
                  if (!selected) return '';
                  const profile = encodingProfiles.find((p) => p.id === selected);
                  return profile ? profile.name : '';
                }}
              >
                {encodingProfiles
                  .filter((profile) => profile.is_active)
                  .map((profile) => {
                    // Build a one-line spec the operator can read at a
                    // glance — codec / CRF / preset / resolution. Means
                    // they don't have to open /settings/profiles to
                    // verify what "Medium" actually is.
                    const specBits = [];
                    if (profile.video_codec) specBits.push(profile.video_codec);
                    if (profile.crf != null) specBits.push(`CRF ${profile.crf}`);
                    if (profile.preset) specBits.push(profile.preset);
                    if (profile.output_resolution) specBits.push(profile.output_resolution);
                    const spec = specBits.join(' · ');
                    return (
                      <MenuItem key={profile.id} value={profile.id}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                          <Box>
                            <strong>{profile.name}</strong>
                            {profile.is_default && (
                              <Typography component="span" variant="caption"
                                sx={{ ml: 1, color: 'success.main', fontWeight: 500 }}>
                                Default
                              </Typography>
                            )}
                          </Box>
                          {spec && (
                            <Typography variant="caption" color="text.secondary">
                              {spec}
                            </Typography>
                          )}
                          {profile.description && (
                            <Typography variant="caption" color="text.disabled">
                              {profile.description}
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          </Tooltip>
        </Box>
        {disableWatermark && (
          <Alert severity="error" sx={{ mt: 1 }}>
            <strong>Leak risk:</strong> the encode will ship with no recipient
            name, email, or unique ID visible on the picture. If it leaks,
            there is no way to trace it back to{' '}
            <strong>{recipientName.trim() || 'this recipient'}</strong>. Use
            only when the recipient has explicitly required a clean copy.
          </Alert>
        )}
      </Paper>

      {/* v10.21.0: optional batch label + custom email message. Producer
          (Marty's bug #65) wanted to label the batch and personalize
          the recipient email without going through the Re-deliver
          dialog. Both batch-level — every recipient gets the same. */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Email & Label</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Project Name defaults to the active campaign and shows in the email subject. Message is optional, rides above the watch / download block. Both apply to every recipient in this batch.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Project Name"
            placeholder="Sundance press cut"
            value={projectNameOverride}
            onChange={(e) => {
              projectNameTouched.current = true;
              setProjectNameOverride(e.target.value.slice(0, 255));
            }}
            fullWidth
            helperText="Defaults to the active campaign name. Used in the delivery email subject. Falls back to the source filename when blank."
          />
          <TextField
            label="Message to recipient (optional)"
            placeholder={'Short paragraph that prepends to the email body. e.g. "Sharing the festival cut ahead of the SF run, would love your thoughts. A."'}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            helperText="Goes to every recipient in this batch. Plain text; newlines preserved."
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recipient Information
        </Typography>
        <Alert severity="info" icon={false} sx={{ mb: 2, py: 0.5 }}>
          You'll be CC'd
          {userInfo?.email ? <> at <strong>{userInfo.email}</strong></> : ''}
          {' '}on every recipient delivery email so you have a copy of
          exactly what they received.
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Tooltip title="Enter the full name of the person receiving this Watermark Encode" arrow placement="top">
            <TextField
              id="recipient-name"
              fullWidth
              label="Recipient Name"
              name="recipientName"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              required
              error={recipientName && !isValidName(recipientName)}
              helperText={
                recipientName && !isValidName(recipientName)
                  ? `Contains invalid characters. Suggested: ${sanitizeName(recipientName)}`
                  : "This name will appear in the watermark"
              }
            />
          </Tooltip>
          
          <Tooltip title="Provide a valid email address for the recipient" arrow placement="top">
            <TextField
              id="recipient-email"
              fullWidth
              label="Recipient Email"
              name="recipientEmail"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              required
              error={recipientEmail && !validateEmail(recipientEmail).valid}
              helperText={
                recipientEmail && !validateEmail(recipientEmail).valid
                  ? validateEmail(recipientEmail).error
                  : "Email address for tracking and notifications"
              }
            />
          </Tooltip>
        </Box>

        {/* Additional recipients. Each row creates ONE additional job
            per source file with that recipient's name baked into the
            watermark — that's the whole point of per-recipient
            watermarking, and the only way a leak can be traced back
            to a specific person. Adding 3 recipients to a 2-source
            batch creates 6 jobs (3 × 2). */}
        {additionalRecipients.map((r, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label={`Recipient #${idx + 2} Name`}
              value={r.name}
              onChange={(e) => {
                const v = e.target.value;
                setAdditionalRecipients((prev) =>
                  prev.map((row, i) => (i === idx ? { ...row, name: v } : row)),
                );
              }}
              error={!!r.name && !isValidName(r.name)}
              helperText={
                r.name && !isValidName(r.name)
                  ? `Suggested: ${sanitizeName(r.name)}`
                  : "Each recipient gets their own uniquely-watermarked encode"
              }
            />
            <TextField
              fullWidth
              label={`Recipient #${idx + 2} Email`}
              type="email"
              value={r.email}
              onChange={(e) => {
                const v = e.target.value;
                setAdditionalRecipients((prev) =>
                  prev.map((row, i) => (i === idx ? { ...row, email: v } : row)),
                );
              }}
              error={!!r.email && !validateEmail(r.email).valid}
              helperText={
                r.email && !validateEmail(r.email).valid
                  ? validateEmail(r.email).error
                  : ' '
              }
            />
            <Tooltip title="Remove this recipient" arrow>
              <IconButton
                aria-label={`Remove recipient ${idx + 2}`}
                onClick={() => {
                  setAdditionalRecipients((prev) => prev.filter((_, i) => i !== idx));
                }}
                sx={{ mt: 1 }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Tooltip
            title="Each additional recipient gets a separately-watermarked encode of every selected source. Total jobs created = (recipients) × (selected sources). Only this exact-recipient watermark can identify a leak back to one person — that's why we don't reuse one encode for many people."
            arrow placement="top"
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setAdditionalRecipients((prev) => [...prev, { name: '', email: '' }])}
            >
              + Add recipient
            </Button>
          </Tooltip>
          {(() => {
            const totalRecipients = 1 + additionalRecipients.filter((r) => (r.name || '').trim() || (r.email || '').trim()).length;
            const totalJobs = totalRecipients * Math.max(1, selectedFiles.length);
            if (totalRecipients === 1 && selectedFiles.length <= 1) return null;
            return (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {totalRecipients} recipient{totalRecipients === 1 ? '' : 's'}{' '}
                ×{' '}{selectedFiles.length || 0} source{selectedFiles.length === 1 ? '' : 's'}{' '}
                = <strong>{totalJobs}</strong> encode{totalJobs === 1 ? '' : 's'}
              </Typography>
            );
          })()}
        </Box>

        {/* Per-job link expiration. Default 30 days = workspace default
            in services/job_assignment.py. Quick-pick chips cover the
            common windows; the number field accepts any value 1–365. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 120 }}>
            Link expires in
          </Typography>
          <TextField
            type="number"
            size="small"
            value={shareExpiresDays}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              setShareExpiresDays(Number.isFinite(n) ? n : 0);
            }}
            inputProps={{ min: 1, max: 365, step: 1, 'aria-label': 'Days until link expires' }}
            sx={{ width: 92 }}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            days
          </Typography>
          {[7, 14, 30, 60, 90].map((d) => (
            <Chip
              key={d}
              label={`${d}d`}
              size="small"
              variant={shareExpiresDays === d ? 'filled' : 'outlined'}
              color={shareExpiresDays === d ? 'primary' : 'default'}
              onClick={() => setShareExpiresDays(d)}
              clickable
            />
          ))}
          <Tooltip title="The watch link stops working after this many days. Recipients who haven't watched yet will see an 'Expired' page. You can extend any share's expiration later from the Library." arrow>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              (default 30 — applies to every recipient in this submit)
            </Typography>
          </Tooltip>
        </Box>

        {/* All four delivery toggles in one column. FormControlLabel
            defaults to inline-flex, which lets the browser pack
            multiple onto a row until they wrap — that produced the
            3-and-1 layout the user flagged. The flex-column wrapper
            forces vertical stacking, and each row's `display: 'flex'`
            keeps the click target sized to the label, not stretched
            full-width. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mt: 1 }}>
          <Tooltip
            title={autoSendEmail
              ? "When the encode + sync finish, the system emails the recipient automatically."
              : "When the encode + sync finish, the system emails YOU the share link so you can forward it however you like."}
            arrow placement="top"
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoSendEmail}
                  onChange={(e) => setAutoSendEmail(e.target.checked)}
                  name="autoSendEmail"
                />
              }
              label="Email the recipient automatically when the encode finishes (otherwise the link comes to you)"
            />
          </Tooltip>

          <Tooltip title="Recipients always get a watch link. Enable this to also let them download the original .mp4." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowDownload}
                  onChange={(e) => setAllowDownload(e.target.checked)}
                  name="allowDownload"
                />
              }
              label="Allow recipient to download the .mp4 (default: watch-only)"
            />
          </Tooltip>

          <Tooltip title="Recipients can leave timestamped notes for the filmmakers while watching. The player labels this section 'Comments to Filmmakers' so the recipient knows where their feedback is going. You'll get a daily digest if there are new notes." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowReviews}
                  onChange={(e) => setAllowReviews(e.target.checked)}
                  name="allowReviews"
                />
              }
              label="Allow recipient to send notes to the filmmakers (timestamped on the video frame)"
            />
          </Tooltip>

          <Tooltip title="Recipients can grab clean (un-watermarked) stills from the source via the watch player. Useful for press/awards publicity. Each capture is audit-logged with timecode + IP." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={allowFrameCapture}
                  onChange={(e) => setAllowFrameCapture(e.target.checked)}
                  name="allowFrameCapture"
                  color="warning"
                />
              }
              label="Allow recipient to capture clean stills from the source (no overlay on stills)"
            />
          </Tooltip>

          {/* v10.21.17: NDA toggle. Default ON. Off skips the
              confidentiality gate on the landing page entirely — the
              recipient lands straight on the player / download. Useful
              for trusted recipients where the prompt is friction
              without value (internal review, retained talent agent,
              etc.). */}
          <Tooltip title="When on (default), the recipient sees a confidentiality agreement on the landing page before they can watch or download. Turn off for trusted recipients where the prompt is friction without value." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={ndaRequired}
                  onChange={(e) => setNdaRequired(e.target.checked)}
                  name="ndaRequired"
                />
              }
              label="Require confidentiality agreement before viewing (recommended)"
            />
          </Tooltip>
        </Box>
        {allowFrameCapture && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <strong>Captures are clean.</strong> Stills don't carry the
            recipient overlay — only the audit log ties a leaked still back
            to{' '}
            <strong>{recipientName.trim() || 'this recipient'}</strong>.
            Use for publicity / press / awards workflows where the recipient
            needs reference frames.
          </Alert>
        )}


        {/* Audio fold-down. Auto-on when any selected source is multi-
            channel (>2). Operator can toggle if they want the original
            channel layout preserved. Stays hidden when every source is
            already stereo so we don't clutter the UX. */}
        {(() => {
          if (selectedFiles.length === 0) return null;
          const channelCounts = selectedFiles
            .map((f) => sourceAudio[f]?.channels)
            .filter((c) => typeof c === 'number');
          if (channelCounts.length === 0) return null;
          const max = Math.max(...channelCounts);
          if (max <= 2) return null;
          const layoutLabel = max === 6 ? '5.1' : max === 8 ? '7.1' : `${max}-channel`;
          return (
            <Tooltip title="Default OFF — the original multichannel mix ships as-is. Tick to fold down to AAC stereo (ITU-R BS.775 matrix). Useful when the recipient's player can't render surround cleanly; leave unchecked when fidelity matters more than browser-streaming compat.">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={downmixToStereo}
                    onChange={(e) => setDownmixToStereo(e.target.checked)}
                  />
                }
                label={`Source has ${layoutLabel} audio — fold down to stereo? (default: keep ${layoutLabel})`}
              />
            </Tooltip>
          );
        })()}

      </Paper>

      <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Tooltip title="Discard changes and return to the queue" arrow placement="top">
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => navigate('/screeners')}
          >
            Cancel
          </Button>
        </Tooltip>
        {(() => {
          const missing = [];
          if (!selectedCompany) missing.push('production company');
          if (!selectedTemplate) missing.push('watermark template');
          if (!recipientName.trim()) missing.push('recipient name');
          if (!recipientEmail.trim()) missing.push('recipient email');
          // Additional recipients with anything typed must have BOTH name + email.
          additionalRecipients.forEach((r, i) => {
            const name = (r.name || '').trim();
            const email = (r.email || '').trim();
            if ((name || email) && (!name || !email)) {
              missing.push(`recipient #${i + 2} ${!name ? 'name' : 'email'}`);
            }
          });
          if (selectedFiles.length === 0) missing.push('at least one file');
          const disabled = loading || missing.length > 0;
          const totalRecipients = 1 + additionalRecipients.filter((r) =>
            (r.name || '').trim() && (r.email || '').trim()
          ).length;
          const totalEncodes = totalRecipients * Math.max(1, selectedFiles.length);
          const tip = disabled && !loading
            ? `Missing: ${missing.join(', ')}`
            : `Create ${totalEncodes} watermarked encode${totalEncodes === 1 ? '' : 's'} (${totalRecipients} recipient${totalRecipients === 1 ? '' : 's'} × ${selectedFiles.length} source${selectedFiles.length === 1 ? '' : 's'})`;
          return (
            <Tooltip title={tip} arrow>
              <span>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  onClick={handleSubmit}
                  disabled={disabled}
                >
                  {loading
                    ? 'Creating...'
                    : `Create ${totalEncodes} Watermark Encode${totalEncodes === 1 ? '' : 's'}`}
                </Button>
              </span>
            </Tooltip>
          );
        })()}
      </Box>

      <Dialog
        open={!!disableWarnDialog}
        onClose={handleCancelDisable}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Disable watermark overlay?
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {(() => {
              const n = (disableWarnDialog?.recipients || []).length;
              return n <= 1
                ? <span>This screener will ship with <strong>NO recipient information</strong> baked into the video.</span>
                : <span>All <strong>{n}</strong> screeners will ship with <strong>NO recipient information</strong> baked in. Every recipient gets a byte-identical clean copy — leaks become un-traceable.</span>;
            })()}
          </Alert>
          <DialogContentText sx={{ mb: 1 }}>
            With the overlay disabled, the encode contains no name, email,
            or unique ID. If the file leaks online, you will not be able to
            trace it back to{' '}
            {(() => {
              const recips = disableWarnDialog?.recipients || [];
              if (recips.length <= 1) {
                return <strong>{recips[0]?.name || recipientName.trim() || 'this recipient'}</strong>;
              }
              return <strong>any of the {recips.length} recipients</strong>;
            })()}
            .
          </DialogContentText>
          <DialogContentText>
            {(() => {
              const recips = disableWarnDialog?.recipients || [];
              const total = recips.length * Math.max(1, selectedFiles.length);
              return total === 1
                ? 'Are you sure you want to create this watermark-disabled encode?'
                : `Are you sure you want to create ${total} watermark-disabled encodes?`;
            })()}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDisable}>Cancel</Button>
          <Button
            onClick={handleConfirmDisable}
            variant="contained"
            color="error"
          >
            Yes, create with no overlay
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!dupDialog}
        onClose={handleCancelDuplicate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Already watermarked for this recipient</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <strong>{recipientName}</strong> already has a watermark of
            {dupDialog && dupDialog.duplicates && dupDialog.duplicates.length === 1
              ? ' this source from the last 30 days:'
              : ' the following sources from the last 30 days:'}
          </DialogContentText>
          <List dense>
            {(dupDialog?.duplicates || []).map((d, i) => {
              const j = d.existing_job || {};
              const when = j.created_at ? new Date(j.created_at).toLocaleString() : '';
              return (
                <ListItem key={i} disableGutters>
                  <ListItemText
                    primary={d.requested_source_filename}
                    secondary={`Job #${j.id} · ${j.status || ''} · created ${when}`}
                  />
                </ListItem>
              );
            })}
          </List>
          <DialogContentText sx={{ mt: 2 }}>
            Continue and create another watermark anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDuplicate}>Cancel</Button>
          <Button onClick={handleConfirmDuplicate} variant="contained" color="warning">
            Create anyway
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default CreateJob;